import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { useInvitationsMutation } from '@/modules/employees/hooks/useInvitationsMutation';
import { useGetTeams } from '@/modules/employees/hooks/useTeams';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Switch,
  TextField,
  TextFieldProps,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { InviteType } from '../../types';

export type InviteUserFormValues = {
  email: string;
  role: string;
  team?: string;
  inviteType: InviteType;
};

type InviteUserDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle' | 'actions'
> & {
  onSuccess?: () => void;
};

const FORM_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .email('Please provide an valid e-mail address')
    .required('Please input your email address'),
  role: yup.string().required().oneOf(['user', 'employer']),
  team: yup.string().optional(),
  inviteType: yup.string().required().oneOf(Object.values(InviteType)),
});

export function InviteUserDialog({
  onSuccess,
  onClose,
  ...props
}: InviteUserDialog) {
  const { data: { data: teams = [] } = {} } = useGetTeams({
    page: 1,
    limit: 100000,
  });

  const { mutateAsync: sendInvitation } = useInvitationsMutation({
    onSuccess: () => {
      enqueueSnackbar('Invitation sent successfully', {
        variant: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      enqueueSnackbar('Failed to send invitation', {
        variant: 'error',
      });
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteUserFormValues>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      email: '',
      role: 'user',
      team: '',
      inviteType: InviteType.User,
    },
  });

  const handleFormSubmit = async (data: InviteUserFormValues) => {
    try {
      await sendInvitation(data);
      reset();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { data },
      });
    }
  };

  return (
    <Modal
      {...props}
      title="Invite User"
      subTitle="Invite a single employee to your organization"
      onClose={(event, reason) => onClose?.(event, reason)}
      classes={{
        paper: 'max-w-xl mx-auto p-8 pb-3',
      }}
      actions={[
        <Button
          key="cancel"
          variant="outlined"
          onClick={() => onClose?.({}, 'backdropClick')}
          disabled={isSubmitting}
        >
          Cancel
        </Button>,
        <Button
          key="invite"
          variant="contained"
          type="submit"
          form="invite-user-form"
          loading={isSubmitting}
        >
          Invite
        </Button>,
      ]}
    >
      <form
        id="invite-user-form"
        className="flex flex-col w-full gap-6"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <label htmlFor="email" className="text-sm inline-block mb-3">
              Email
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  size="small"
                  {...(field as TextFieldProps)}
                  slotProps={{
                    input: {
                      id: 'email',
                    },
                  }}
                  error={!!errors.email}
                  placeholder="Email"
                  type="email"
                />
              )}
            />
          </div>
          <div className="flex flex-col flex-1 items-center min-w-[100px] self-end">
            <Controller
              name="team"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 100, width: '100%' }}>
                  <Select
                    {...field}
                    labelId="invitation-select-team"
                    onChange={e => field.onChange(e.target.value)}
                    displayEmpty
                    renderValue={selected => {
                      if (!selected) {
                        return 'Team';
                      }
                      return (
                        teams.find(t => t._id === selected)?.name ?? selected
                      );
                    }}
                  >
                    {field.value && (
                      <MenuItem value="">Remove from team</MenuItem>
                    )}
                    {teams.length === 0 ? (
                      <MenuItem value="" disabled>
                        No teams available
                      </MenuItem>
                    ) : (
                      teams.map(t => (
                        <MenuItem key={t._id} value={t._id}>
                          {t.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div className="flex flex-col ml-auto justify-end min-w-[80px]">
            <label htmlFor="role" className="text-sm inline-block mb-3">
              Admin
            </label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Switch
                  slotProps={{
                    input: {
                      id: 'role',
                    },
                  }}
                  defaultChecked={field.value === 'employer'}
                  onChange={e => {
                    field.onChange(e.target.checked ? 'employer' : 'user');
                  }}
                />
              )}
            />
          </div>
        </div>
        {errors.email?.message && (
          <p className="text-red-500 text-sm">{errors.email?.message}</p>
        )}
      </form>
    </Modal>
  );
}
